// Test your Megascans wood floor texture
import React, { Suspense, useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import {  OrbitControls, Environment, ContactShadows, Sky, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';

export default function TestWoodFloor() {
  return (
    <div style={{ width: '100vw', height: '100vh', background: '#1a1a1a' }}>
      <Canvas shadows>
        <PerspectiveCamera makeDefault position={[8, 5, 8]} fov={50} />
        <OrbitControls
          target={[0, 0, 0]}
          maxPolarAngle={Math.PI / 2.2}
          minDistance={3}
          maxDistance={20}
        />

        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight
          position={[10, 15, 10]}
          intensity={1.5}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        <pointLight position={[-5, 5, -5]} intensity={0.5} />

        <Sky sunPosition={[10, 15, 10]} />
        <Environment preset="apartment" />

        {/* Your Megascans Wood Floor */}
        <Suspense fallback={<SimpleFloor />}>
          <MegascansWoodFloor />
        </Suspense>

        {/* Test Box to see shadows */}
        <mesh position={[0, 1, 0]} castShadow>
          <boxGeometry args={[2, 2, 2]} />
          <meshStandardMaterial color="#8B4513" roughness={0.7} />
        </mesh>

        <ContactShadows
          position={[0, 0.01, 0]}
          opacity={0.5}
          scale={20}
          blur={2}
        />
      </Canvas>

      {/* UI */}
      <div style={{
        position: 'absolute',
        top: 20,
        left: 20,
        background: 'rgba(0,0,0,0.8)',
        padding: '20px',
        borderRadius: '10px',
        color: 'white',
        fontFamily: 'monospace'
      }}>
        <h2 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>🪵 Megascans Wood Floor Test</h2>
        <p style={{ margin: '5px 0', fontSize: '12px' }}>
          Drag to rotate • Scroll to zoom
        </p>
        <p style={{ margin: '5px 0', fontSize: '12px', color: '#4ade80' }}>
          ✅ 2K Resolution PBR Textures
        </p>
        <p style={{ margin: '5px 0', fontSize: '10px', color: '#888' }}>
          BaseColor • Normal • Roughness • AO
        </p>
      </div>
    </div>
  );
}

function MegascansWoodFloor() {
  const [material, setMaterial] = useState<THREE.MeshStandardMaterial | null>(null);
  const [loadingStatus, setLoadingStatus] = useState('Loading...');

  useEffect(() => {
    const textureLoader = new THREE.TextureLoader();
    const basePath = '/assets/fab/textures/wood/wooden_floor';

    // Load all Megascans textures
    const loadTextures = async () => {
      try {
        setLoadingStatus('Loading BaseColor...');
        const baseColorTexture = await loadTexture(`${basePath}/Wooden_Floor_wdipfjw_2K_BaseColor.jpg`);
        baseColorTexture.colorSpace = THREE.SRGBColorSpace;
        baseColorTexture.wrapS = THREE.RepeatWrapping;
        baseColorTexture.wrapT = THREE.RepeatWrapping;
        baseColorTexture.repeat.set(5, 5);

        setLoadingStatus('Loading Normal map...');
        const normalTexture = await loadTexture(`${basePath}/Wooden_Floor_wdipfjw_2K_Normal.jpg`);
        normalTexture.wrapS = THREE.RepeatWrapping;
        normalTexture.wrapT = THREE.RepeatWrapping;
        normalTexture.repeat.set(5, 5);

        setLoadingStatus('Loading Roughness map...');
        const roughnessTexture = await loadTexture(`${basePath}/Wooden_Floor_wdipfjw_2K_Roughness.jpg`);
        roughnessTexture.wrapS = THREE.RepeatWrapping;
        roughnessTexture.wrapT = THREE.RepeatWrapping;
        roughnessTexture.repeat.set(5, 5);

        setLoadingStatus('Loading AO map...');
        const aoTexture = await loadTexture(`${basePath}/Wooden_Floor_wdipfjw_2K_AO.jpg`);
        aoTexture.wrapS = THREE.RepeatWrapping;
        aoTexture.wrapT = THREE.RepeatWrapping;
        aoTexture.repeat.set(5, 5);

        setLoadingStatus('✅ Loaded!');

        // Create PBR material
        const mat = new THREE.MeshStandardMaterial({
          map: baseColorTexture,
          normalMap: normalTexture,
          normalScale: new THREE.Vector2(1, 1),
          roughnessMap: roughnessTexture,
          roughness: 1,
          aoMap: aoTexture,
          aoMapIntensity: 1,
        });

        setMaterial(mat);
      } catch (error) {
        console.error('Failed to load textures:', error);
        setLoadingStatus('❌ Failed to load');
      }
    };

    loadTextures();

    function loadTexture(path: string): Promise<THREE.Texture> {
      return new Promise((resolve, reject) => {
        textureLoader.load(
          path,
          resolve,
          undefined,
          reject
        );
      });
    }
  }, []);

  if (!material) {
    return <SimpleFloor />;
  }

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <planeGeometry args={[30, 30]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}

function SimpleFloor() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <planeGeometry args={[30, 30]} />
      <meshStandardMaterial color="#d4a574" />
    </mesh>
  );
}
